/* eslint-disable react/no-unused-prop-types */
import type { ReactNode } from 'react';
import { lazy, useEffect, useRef } from 'react';
import _defaultsDeep from 'lodash/defaultsDeep';
// import { Loader } from '@tmsw/powerups.ui.loaders.base';
import { useStore } from '@tmsw/powerups.store';
import type {
  SupportedLocales,
  FallbackLocale,
  RollupLocales,
  IntlProviderOptions,
} from './services/I18n';
import { I18n } from './services/I18n';
import type { LocaleConfigObject } from './context';
import { I18nContext, getDefaultValue } from './context';

const Loader = lazy(() =>
  import('@tmsw/powerups.ui.loaders.base').then((module) => ({
    default: module.Loader,
  }))
);

export type ProviderProps = {
  children: ReactNode;
  supportedLocales?: SupportedLocales;
  fallbackLocale: FallbackLocale;
  rollupLocales?: RollupLocales;
  apiService?: (
    locale: Intl.UnicodeBCP47LocaleIdentifier,
    options?: Pick<IntlProviderOptions, 'apiService'>
  ) => Promise<LocaleConfigObject>;
  providerOptions?: IntlProviderOptions;
};
export function IntlProvider(props: ProviderProps) {
  const defaultCtxValue = useRef(getDefaultValue());

  const {
    children,
    apiService,
    supportedLocales,
    fallbackLocale,
    rollupLocales,
    providerOptions,
  } = {
    ...defaultCtxValue.current,
    ...props,
  };

  const i18n = new I18n(supportedLocales, fallbackLocale);

  const localeRef = useRef<string>();
  const localeConfigInit = useRef(false);
  const loadedConfigs = useRef<Map<string, { [key: string]: any }>>(
    new Map([])
  );

  const setLocaleConfig = useStore((state) => state.setLocaleConfig);
  const localeConfig = useStore((state) => state.localeConfig);
  const locale = useStore((state) => state.locale);
  const localeLoading = useStore((state) => state.localeLoading);

  async function loadLocaleConfig(
    newLocale: Intl.UnicodeBCP47LocaleIdentifier
  ) {
    const options = { ...providerOptions.apiService };
    console.group('Intl :: loadLocaleConfig');
    // console.log('apiService', apiService);
    // console.log('newLocale', newLocale);
    // console.log('options', options, providerOptions.apiService);
    if (!newLocale) return; // nothing to do

    // split the segments to provide rollup support for fallbackLocale
    const localeSegments = newLocale.split('-');
    // set a legacy rollupLocale to retro-fit existing programs not using rollups

    const legacyRollupLocales: RollupLocales = new Map([
      localeSegments.length > 1 && localeSegments[0] === fallbackLocale
        ? [newLocale, ['global', fallbackLocale, newLocale]]
        : [newLocale, ['global', newLocale]],
    ]);

    const currRollupLocales =
      typeof rollupLocales !== 'undefined'
        ? rollupLocales
        : legacyRollupLocales;

    try {
      // get the roll-up config for the locale we want to load
      const rollupLocale = currRollupLocales.get(newLocale);
      console.log('rollupLocale', rollupLocale);

      // create a map of the local configs that are not already loaded into memory
      const loadConfigPromiseMap = new Map(
        rollupLocale
          .filter((rLocale) => {
            return !loadedConfigs.current.has(rLocale);
          })
          // @ts-ignore
          .map((rLocale) => [rLocale, apiService(rLocale, options)])
      );

      const promiseRes = await Promise.all(loadConfigPromiseMap.values());
      promiseRes.forEach((obj, index) => {
        loadedConfigs.current.set(obj.locale, obj);
      });
      console.log('loadedConfigs', loadedConfigs);

      // create a new config object to merge into; initalize with the first locale (our newLocale value)
      const loadedConfig = {};

      while (rollupLocale.length > 0) {
        const rLocale = rollupLocale.pop();
        const nextLocaleConfig = loadedConfigs.current.get(rLocale);
        console.info(`Rolling in ${nextLocaleConfig.locale}`);
        _defaultsDeep(loadedConfig, nextLocaleConfig);
      }

      document.querySelector('html').setAttribute('lang', newLocale);
      setLocaleConfig(loadedConfig);
      console.info('loadedConfig', loadedConfig);
      console.groupEnd();
    } catch (e: unknown) {
      /* eslint-disable-next-line no-console */
      console.error('ERROR', String(e));
      console.groupEnd();
    }
  }

  const updateLocale = (newLocale) => {
    localeRef.current = newLocale;
    loadLocaleConfig(localeRef.current);
  };

  // const handleLanguageChange = (e:any) => {
  //     console.log("languagechange event detected!", e);
  // };

  // const handlePopState = (e:any) => {
  //   console.log('handlePopState', e);
  //   const spLocale = new URLSearchParams(window?.location?.search).get('locale');
  //   if (!spLocale) return;
  //   const foundLocale = i18n.supportedLocales.has(spLocale);
  //   if (!foundLocale) return;
  //   if (foundLocale && spLocale !== localeRef.current) {
  //     localeRef.current = spLocale;
  //     loadLocaleConfig(spLocale);
  //   }
  // };

  useEffect(() => {
    if (typeof locale !== 'undefined' && locale !== localeRef.current) {
      console.log('Updating locale to ', locale);
      updateLocale(locale);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  useEffect(() => {
    if (localeConfigInit.current) return;
    if (typeof localeRef.current === 'undefined') {
      const foundLocale = i18n.determineLocale();
      if (foundLocale) {
        localeConfigInit.current = true;
        localeRef.current = foundLocale;
        loadLocaleConfig(localeRef.current);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect(() => {
  //   window.addEventListener("languagechange", handleLanguageChange);
  //   return () => {
  //     window.removeEventListener("languagechange", handleLanguageChange);
  //   };
  // }, []);

  // useEffect(() => {
  //   window.addEventListener("popstate", handlePopState);
  //   return () => {
  //     window.removeEventListener("popstate", handlePopState);
  //   };
  // }, []);

  return !localeLoading ? (
    <I18nContext.Provider
      value={{
        ...defaultCtxValue.current,
        supportedLocales,
        fallbackLocale,
        updateLocale,
        ...localeConfig,
      }}
    >
      {children}
    </I18nContext.Provider>
  ) : (
    <Loader />
  );
}

IntlProvider.whyDidYouRender = true;

export default IntlProvider;
